import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Button, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  LinkOff as RevokeIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import SearchableDataTable from '../components/SearchableDataTable';
import '../styles/EntityManagement.css';
import './Access.css';
import siteConfig from '../config/siteConfig';
import { API_BASE_URL } from '../services/api';

const Access = () => {
  const [users, setUsers] = useState([]);
  const roles = ['admin', 'tax_manager', 'viewer'];
  const getDefaultAccessLevel = (role) => (
    siteConfig.isGlobal && role === 'admin' ? 'global' : 'entity'
  );
  const getRoleLabel = (role) => {
    if (role === 'admin') {
      return siteConfig.isGlobal ? 'Platform Admin' : 'Site Admin';
    }
    if (role === 'tax_manager') return 'Manager';
    if (role === 'viewer') return 'Viewer';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'viewer',
    accessLevel: getDefaultAccessLevel('viewer')
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Handle removing selected users
  const handleRemoveUsers = async () => {
    if (!selectedUsers.length) {
      setNotification({
        open: true,
        message: 'Please select at least one user to remove',
        severity: 'warning'
      });
      return;
    }

    const numSelected = selectedUsers.length;
    const message = numSelected === 1 
      ? 'Are you sure you want to delete this user?' 
      : `Are you sure you want to delete ${numSelected} users?`;
    if (!window.confirm(message)) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token. Please log in.');

      // Remove users one by one
      for (const userId of selectedUsers) {
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to remove user');
        }

        // Update the UI after successful removal
        setUsers(users.filter(user => !selectedUsers.includes(user.id)));
        setSelectedUsers([]);
      }

      // Update users list
      setUsers(users.filter(user => !selectedUsers.includes(user.id)));
      setSelectedUsers([]);

      setNotification({
        open: true,
        message: 'Users removed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Remove users error:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to remove users',
        severity: 'error'
      });
    }
  };

  // Handle adding a new user
  const handleAddUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token. Please log in.');

      const {
        firstName,
        lastName,
        email,
        role,
        organizationId,
        entityId,
        accessLevel
      } = newUser;

      // Basic frontend validations
      if (!firstName || !lastName || !email) {
        alert('All required fields must be filled.');
        return;
      }

      // Email format check
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert('Email must be valid.');
        return;
      }

      // Build payload
      const payload = {
        firstName,
        lastName,
        email,
        role: role || 'viewer',
        accessLevel: accessLevel || getDefaultAccessLevel(role || 'viewer'),
      };

      // Optional fields, can be implemented later
      if (organizationId) payload.organizationId = organizationId;
      if (entityId) payload.entityId = entityId;
      if (accessLevel) payload.accessLevel = accessLevel;

      const response = await fetch(`${API_BASE_URL}/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Validation Error');
      }

      setUsers([...users, result.user]);
      setInviteLink(result.inviteUrl || '');
      setOpenDialog(false);
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'viewer',
        organizationId: '',
        entityId: '',
        accessLevel: getDefaultAccessLevel('viewer')
      });
      setNotification({
        open: true,
        message: result.emailDelivery?.failed
          ? 'Invite created, but email failed. Copy the setup link from the dialog.'
          : result.emailDelivery?.skipped
            ? 'Invite created. Copy the setup link from the dialog.'
            : 'Invite created and emailed.',
        severity: result.emailDelivery?.failed ? 'warning' : 'success'
      });
    } catch (err) {
      console.error('Add user error:', err);
      alert(err.message || 'Failed to add user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token. Please log in.');

      const {
        firstName,
        lastName,
        email,
        password,
        role,
        organizationId,
        entityId,
        accessLevel
      } = newUser;

      // Basic frontend validations
      if (!firstName || !lastName || !email) {
        alert('All required fields must be filled.');
        return;
      }

      // Email format check
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert('Email must be valid.');
        return;
      }

      // Build payload
      const payload = {
        firstName,
        lastName,
        email,
        password,
        role: role || 'viewer',
        accessLevel: accessLevel || getDefaultAccessLevel(role || 'viewer'),
      };

      // Optional fields, can be implemented later
      if (organizationId) payload.organizationId = organizationId;
      if (entityId) payload.entityId = entityId;
      if (accessLevel) payload.accessLevel = accessLevel;

      const response = await fetch(`${API_BASE_URL}/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Validation Error');
      }

      // Update users list
      setUsers(users.map(user => 
        user.id === selectedUser.id ? result.user : user
      ));

      setOpenDialog(false);
      setSelectedUser(null);
      setEditMode(false);
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'viewer',
        organizationId: '',
        entityId: '',
        accessLevel: getDefaultAccessLevel('viewer')
      });
    } catch (err) {
      console.error('Update user error:', err);
      alert(err.message || 'Failed to update user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditMode(true);
    setNewUser({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      organizationId: user.organizationId || '',
      entityId: user.entityId || '',
      accessLevel: user.accessLevel || getDefaultAccessLevel(user.role)
    });
    setOpenDialog(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token. Please log in.');

      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Delete user error:', error);
      alert('Failed to delete user');
    }
  };

  const handleCopyInviteLink = async (user) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token. Please log in.');

      const response = await fetch(`${API_BASE_URL}/users/${user.id}/resend-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Failed to create invite link');
      }

      await navigator.clipboard.writeText(result.inviteUrl);
      setInviteLink(result.inviteUrl || '');
      setUsers(users.map(existingUser => (
        existingUser.id === user.id ? { ...existingUser, ...result.user } : existingUser
      )));
      setNotification({
        open: true,
        message: result.emailDelivery?.failed
          ? 'Invite link copied, but email failed.'
          : result.emailDelivery?.skipped
            ? 'Invite link copied'
            : 'Invite link copied and emailed.',
        severity: result.emailDelivery?.failed ? 'warning' : 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to copy invite link',
        severity: 'error'
      });
    }
  };

  const handleRevokeInvite = async (user) => {
    if (!window.confirm(`Revoke invite for ${user.email}?`)) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token. Please log in.');

      const response = await fetch(`${API_BASE_URL}/users/${user.id}/revoke-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Failed to revoke invite');
      }

      setUsers(users.map(existingUser => (
        existingUser.id === user.id ? { ...existingUser, ...result.user } : existingUser
      )));
      setNotification({
        open: true,
        message: 'Invite revoked',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to revoke invite',
        severity: 'error'
      });
    }
  };

  const handleInputChange = (e) => {
    setNewUser({
      ...newUser,
      [e.target.name]: e.target.value
    });
  };

  const handleRoleChange = (e) => {
    const role = e.target.value;
    setNewUser({
      ...newUser,
      role,
      accessLevel: getDefaultAccessLevel(role)
    });
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedUser(null);
    setNewUser({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'viewer',
      organizationId: '',
      entityId: '',
      accessLevel: getDefaultAccessLevel('viewer')
    });
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found. Please log in.');
        }

        const response = await fetch(`${API_BASE_URL}/users`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => {});
          throw new Error(errorData?.message || 'Failed to fetch users');
        }

        const result = await response.json();
        setUsers(result.users || []);
      } catch (error) {
        console.error('Error fetching users:', error);
        alert(error.message);
      }
    };

    fetchUsers();
  }, []);

  return (
    <Box className="entity-management-container">
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <Box className="toolbar-container">
        <Box className="selection-info" sx={{ pl: 0 }}>
          <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>Users</Typography>
          {selectedUsers.length > 0 && (
            <Typography variant="body2" className="selection-info" sx={{ ml: 2 }}>
              {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
            </Typography>
          )}
        </Box>
        <Box className="button-group">
          <Button
            className="action-button"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              minWidth: 90,
              width: 'fit-content',
              padding: '6px 16px',
              border: '2px solid var(--color-primary)',
              color: 'var(--color-black)',
              '&:hover': {
                backgroundColor: 'var(--color-dark)',
                color: 'var(--color-white)',
                borderColor: 'var(--color-dark)'
              },
              '& .MuiButton-startIcon': {
                color: 'var(--color-primary)',
              }
            }}
          >
            Add
          </Button>
          <Button
            className="action-button"
            startIcon={<RemoveIcon />}
            onClick={handleRemoveUsers}
            disabled={!selectedUsers.length}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              minWidth: 90,
              width: 'fit-content',
              padding: '6px 16px',
              '&.Mui-disabled': {
                opacity: 0.4,
              }
            }}
          >
            Remove
          </Button>
        </Box>
      </Box>
      <Box className="main-content">
        <Box className="table-container">
          <SearchableDataTable
            searchPlaceholder="Search Users..."
            rows={users || []}
            columns={[
              { field: 'firstName', headerName: 'First Name', flex: 1 },
              { field: 'lastName', headerName: 'Last Name', flex: 1 },
              { field: 'email', headerName: 'Email', flex: 1.5 },
              {
                field: 'inviteStatus',
                headerName: 'Invite',
                flex: 0.8,
                valueGetter: (params) => {
                  if (params.row.inviteAcceptedAt) return 'Accepted';
                  if (params.row.inviteRevokedAt) return 'Revoked';
                  if (params.row.inviteExpiresAt && new Date(params.row.inviteExpiresAt) < new Date()) return 'Expired';
                  if (params.row.inviteExpiresAt) return 'Pending';
                  return params.row.isActive ? 'Active' : 'Inactive';
                },
                renderCell: (params) => (
                  <Chip
                    label={params.value}
                    size="small"
                    color={params.value === 'Pending' ? 'warning' : params.value === 'Accepted' || params.value === 'Active' ? 'success' : 'default'}
                    variant={params.value === 'Pending' ? 'filled' : 'outlined'}
                  />
                )
              },
              { 
                field: 'role', 
                headerName: 'Role', 
                flex: 0.8,
                renderCell: (params) => {
                  if (!params.value) return null;
                  const role = params.value;
                  return (
                    <Chip label={getRoleLabel(role)} size="small" />
                  );
                }
              },
              { 
                field: 'actions', 
                headerName: 'Actions', 
                width: 140,
                renderCell: (params) => {
                  if (!params.row) return null;
                  const hasPendingInvite = params.row.inviteExpiresAt &&
                    !params.row.inviteAcceptedAt &&
                    !params.row.inviteRevokedAt;

                  return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0 }}>
                    {hasPendingInvite && (
                      <IconButton size="small" onClick={() => handleCopyInviteLink(params.row)} title="Copy Invite Link">
                        <CopyIcon sx={{ width: 18, height: 18 }} />
                      </IconButton>
                    )}
                    {hasPendingInvite && (
                      <IconButton size="small" onClick={() => handleRevokeInvite(params.row)} title="Revoke Invite">
                        <RevokeIcon sx={{ width: 18, height: 18 }} />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => handleEditUser(params.row)} title="Edit User">
                      <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteUser(params.row.id)} title="Delete User">
                      <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
                    </IconButton>
                  </Box>
                  );
                }
              }
            ]}
            selectionModel={selectedUsers}
            onSelectionModelChange={(newSelection) => setSelectedUsers(newSelection || [])}
            checkboxSelection
            disableSelectionOnClick
            height="100%"
            tableName="users"
          />
        </Box>
      </Box>
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit User' : 'Invite User'}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={newUser.firstName}
                onChange={handleInputChange}
                name="firstName"
                fullWidth
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={newUser.lastName}
                onChange={handleInputChange}
                name="lastName"
                fullWidth
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                value={newUser.email}
                onChange={handleInputChange}
                name="email"
                fullWidth
                required
                autoComplete="off"
              />
            </Grid>
            {editMode && (
            <Grid item xs={12}>
              <TextField
                label="New Password"
                type="password"
                value={newUser.password}
                onChange={handleInputChange}
                name="password"
                fullWidth
                autoComplete="off"
                helperText="Leave blank to keep the existing password"
              />
            </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUser.role}
                  onChange={handleRoleChange}
                  label="Role"
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {getRoleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={editMode ? handleUpdateUser : handleAddUser} variant="contained">
            {editMode ? 'Update' : 'Create Invite'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(inviteLink)} onClose={() => setInviteLink('')} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Link</DialogTitle>
        <DialogContent>
          <TextField
            value={inviteLink}
            fullWidth
            multiline
            minRows={2}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigator.clipboard.writeText(inviteLink)} startIcon={<CopyIcon />}>
            Copy
          </Button>
          <Button onClick={() => setInviteLink('')}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Access;
